import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, FormControl, IconButton, InputLabel,
  MenuItem, Paper, Select, Slider, Snackbar, Stack, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthModal } from '../../contexts/AuthModalContext';
import api from '../../api';
import PracticalAssessment from '../../components/PracticalAssessment';
import PracticalInstructions from '../../components/PracticalInstructions';
import FieldOfView from './FieldOfView';
import Microscope from './Microscope';
import { type ObjectivePower } from './magnification';
import Notebook, { type MicroscopyObservation } from './Notebook';
import StepGuide from './StepGuide';
import DataAnalytics from './DataAnalytics';
import SlideLibraryAdmin from './SlideLibraryAdmin';
import { PRACTICAL_MODES, SPECIMEN_SLIDES, type PracticalMode, type SpecimenSlide } from './specimens';
import './microscopyLab.css';

const STORAGE_KEY = 'microscopy-lab-onboarded';

interface SavedMicroscopySession { _id: string; title: string; updatedAt?: string; }

const MicroscopyLab: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { openLogin } = useAuthModal();
  const [mode, setMode] = useState<PracticalMode>('explore');
  const [slideId, setSlideId] = useState(SPECIMEN_SLIDES[0].id);
  const [slideCatalog, setSlideCatalog] = useState<SpecimenSlide[]>(SPECIMEN_SLIDES);
  const [objective, setObjective] = useState<ObjectivePower>(4);
  const [focusOffsetUm, setFocusOffsetUm] = useState(0);
  const [stagePan, setStagePan] = useState({ x: 0, y: 0 });
  const [lampOn, setLampOn] = useState(true);
  const [lamp, setLamp] = useState(0.78);
  const [diaphragm, setDiaphragm] = useState(0.72);
  const [condenser, setCondenser] = useState(0.65);
  const [stageClipsOn, setStageClipsOn] = useState(true);
  const [oilImmersion, setOilImmersion] = useState(false);
  const [measuring, setMeasuring] = useState(false);
  const [measurement, setMeasurement] = useState<{ distanceMicrometres: number; uncertaintyMicrometres: number } | null>(null);
  const [snapshotDraft, setSnapshotDraft] = useState('');
  const [observations, setObservations] = useState<MicroscopyObservation[]>([]);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [conclusion, setConclusion] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [savedSessions, setSavedSessions] = useState<SavedMicroscopySession[]>([]);
  const [sessionReady, setSessionReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sessionMessage, setSessionMessage] = useState('Preparing a private lab session…');
  const [darkTheme, setDarkTheme] = useState(false);
  const [highContrast, setHighContrast] = useState(false);
  const [resetNotice, setResetNotice] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const slide = useMemo(() => slideCatalog.find((item) => item.id === slideId) ?? slideCatalog[0], [slideCatalog, slideId]);
  const totalMagnification = 10 * objective;

  useEffect(() => {
    setShowOnboarding(localStorage.getItem(STORAGE_KEY) !== 'true');
  }, []);

  const restoreSession = useCallback((saved: any) => {
    setSessionId(saved._id);
    setMode(saved.mode || 'explore');
    setSlideId(saved.currentSlideId || 'onion-epidermis');
    setObjective(saved.microscope?.objective || 4);
    setFocusOffsetUm(saved.microscope?.focusOffsetUm || 0);
    setLamp(saved.microscope?.lamp ?? 0.78);
    setDiaphragm(saved.microscope?.diaphragm ?? 0.72);
    setCondenser(saved.microscope?.condenser ?? 0.65);
    setStagePan(saved.microscope?.pan || { x: 0, y: 0 });
    setLampOn(saved.microscope?.lampOn ?? true);
    setStageClipsOn(saved.microscope?.stageClipsOn ?? true);
    setOilImmersion(saved.microscope?.oilImmersion ?? false);
    setObservations(saved.observations || []);
    setCompletedSteps(saved.completedSteps || []);
    setConclusion(saved.conclusion || '');
    setSessionReady(true);
    setSessionMessage('Session autosaved to your account.');
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let active = true;
    const initializeSession = async () => {
      setSessionReady(false);
      try {
        const response = await api.get('/microscopy/sessions');
        const sessions = response.data?.data || [];
        if (!active) return;
        setSavedSessions(sessions.map((item: SavedMicroscopySession) => ({ _id: item._id, title: item.title, updatedAt: item.updatedAt })));
        if (sessions.length) {
          const detail = await api.get(`/microscopy/sessions/${sessions[0]._id}`);
          if (active) restoreSession(detail.data.data);
        } else {
          const created = await api.post('/microscopy/sessions', { title: 'Microscopy practical', mode: 'explore' });
          if (!active) return;
          restoreSession(created.data.data);
          setSavedSessions([{ _id: created.data.data._id, title: created.data.data.title, updatedAt: created.data.data.updatedAt }]);
        }
      } catch (error: any) {
        if (active) setSessionMessage(error?.response?.data?.message || 'Session could not be loaded. Changes are not synced yet.');
      }
    };
    initializeSession();
    return () => { active = false; };
  }, [isAuthenticated, restoreSession]);

  useEffect(() => {
    if (!sessionReady || !sessionId) return undefined;
    setSaving(true);
    const timer = window.setTimeout(async () => {
      try {
        const saved = await api.patch(`/microscopy/sessions/${sessionId}`, {
          mode, currentSlideId: slideId,
          microscope: { objective, focusOffsetUm, lamp, diaphragm, condenser, lampOn, stageClipsOn, oilImmersion, pan: stagePan },
          observations, conclusion,
          completedSteps,
        });
        setSessionMessage('All microscope settings and observations are saved to your account.');
        setSavedSessions((current) => current.map((item) => item._id === sessionId ? { ...item, updatedAt: saved.data.data.updatedAt } : item));
      } catch (error: any) {
        setSessionMessage(error?.response?.data?.message || 'Autosave failed. Your current work remains in this browser.');
      } finally { setSaving(false); }
    }, 800);
    return () => window.clearTimeout(timer);
  }, [sessionReady, sessionId, mode, slideId, objective, focusOffsetUm, lamp, diaphragm, condenser, lampOn, stageClipsOn, oilImmersion, stagePan, observations, conclusion, completedSteps]);

  useEffect(() => {
    const practical = PRACTICAL_MODES.find((item) => item.id === mode);
    if (practical && mode !== 'explore' && mode !== 'sandbox') setSlideId(practical.slideId);
    if (mode === 'measurement') setSlideId('stage-micrometer');
  }, [mode]);

  const acknowledgeOnboarding = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setShowOnboarding(false);
  };

  const handleObjectiveChange = (next: ObjectivePower) => {
    setObjective(next);
    setFocusOffsetUm((offset) => Math.max(-8, Math.min(8, offset * (objective / next))));
    if (next !== 100) setOilImmersion(false);
  };

  const handleReset = () => {
    if (!window.confirm('Reset the microscope, slide position, focus, and illumination settings?')) return;
    setObjective(4); setFocusOffsetUm(0); setLampOn(true); setLamp(0.78); setDiaphragm(0.72);
    setCondenser(0.65); setStagePan({ x: 0, y: 0 }); setStageClipsOn(true); setOilImmersion(false); setMeasuring(false); setMeasurement(null);
    setResetNotice(true);
  };

  const handleMeasurement = useCallback((next: typeof measurement) => setMeasurement(next), []);
  const handleSnapshot = useCallback((snapshot: string) => setSnapshotDraft(snapshot), []);

  const refreshSlideCatalog = useCallback(async () => {
    const response = await api.get('/microscopy/slides');
    const dynamicSlides: SpecimenSlide[] = (response.data?.data || []).map((item: any) => ({
      ...item,
      id: item.slideId || item.id,
      imageStacks: item.imageStacks || item.focusStacks,
    }));
    setSlideCatalog((current) => {
      const combined = new Map(current.map((item) => [item.id, item]));
      dynamicSlides.forEach((item) => combined.set(item.id, item));
      return Array.from(combined.values());
    });
  }, []);

  useEffect(() => {
    if (isAuthenticated) refreshSlideCatalog().catch((error) => console.warn('Could not load microscopy slide catalog.', error));
  }, [isAuthenticated, refreshSlideCatalog]);

  const handleResumeSession = async (nextSessionId: string) => {
    try {
      setSessionReady(false);
      const response = await api.get(`/microscopy/sessions/${nextSessionId}`);
      restoreSession(response.data.data);
      setSnapshotDraft(''); setMeasurement(null);
    } catch (error: any) {
      setSessionReady(true);
      setSessionMessage(error?.response?.data?.message || 'Could not resume that lab session.');
    }
  };

  const handleNewSession = async () => {
    try {
      setSessionReady(false);
      const response = await api.post('/microscopy/sessions', { title: 'Microscopy practical', mode: 'explore' });
      restoreSession(response.data.data);
      setSavedSessions((current) => [{ _id: response.data.data._id, title: response.data.data.title, updatedAt: response.data.data.updatedAt }, ...current]);
      setSnapshotDraft(''); setMeasurement(null);
    } catch (error: any) {
      setSessionReady(true);
      setSessionMessage(error?.response?.data?.message || 'Could not create a new session.');
    }
  };

  const handleAddObservation = (observation: MicroscopyObservation) => {
    setObservations((current) => [...current, observation]);
    setSnapshotDraft('');
  };

  const handleUpdateObservation = (id: string, patch: Partial<MicroscopyObservation>) => {
    setObservations((current) => current.map((entry) => (entry._id || entry.timestamp) === id ? { ...entry, ...patch } : entry));
  };

  const handleDeleteObservation = (id: string) => setObservations((current) => current.filter((entry) => (entry._id || entry.timestamp) !== id));

  const handleReportGenerated = async (pdfDataUrl: string) => {
    if (!sessionId) { setSessionMessage('Report downloaded, but no account session is available to save it.'); return; }
    try {
      const response = await api.post(`/microscopy/sessions/${sessionId}/report`, { pdfDataUrl });
      setSessionMessage(`Report saved to your account: ${response.data.data.fileUrl}`);
    } catch (error: any) {
      setSessionMessage(error?.response?.data?.message || 'Report downloaded but could not be saved to your account.');
    }
  };

  if (authLoading) return <Container sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Container>;
  if (!isAuthenticated) return <Container maxWidth="sm" sx={{ py: 8 }}><Alert severity="info" action={<Button color="inherit" onClick={() => openLogin()}>Sign in</Button>}>Sign in to use the Virtual Microscopy Laboratory and save your practical work.</Alert></Container>;

  return (
    <Box className={`microscopy-lab ${darkTheme ? 'microscopy-dark' : ''} ${highContrast ? 'microscopy-high-contrast' : ''}`}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
        <Stack className="micro-header" direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title="Return to Biology practicals"><IconButton onClick={() => navigate('/labs')} aria-label="Back to Biology practicals"><ArrowBackIcon /></IconButton></Tooltip>
            <Box><Typography variant="h4" fontWeight={900}>Virtual Microscopy Laboratory</Typography><Typography color="text.secondary">Explore, focus, measure, and record microscopic specimens.</Typography></Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={`${totalMagnification}× total magnification`} color="primary" />
            <Chip label={saving ? 'Saving…' : sessionId ? 'Autosaved' : 'Session pending'} color={saving ? 'default' : sessionId ? 'success' : 'warning'} />
            <Tooltip title={darkTheme ? 'Switch to light theme' : 'Switch to dark microscope theme'}><IconButton onClick={() => setDarkTheme((value) => !value)} aria-label="Toggle dark microscope theme"><Brightness4Icon /></IconButton></Tooltip>
            <Button size="small" variant={highContrast ? 'contained' : 'outlined'} onClick={() => setHighContrast((value) => !value)} aria-pressed={highContrast}>High contrast</Button>
            <Tooltip title="Reset microscope controls and slide stage"><IconButton onClick={handleReset} aria-label="Reset microscope"><RestartAltIcon /></IconButton></Tooltip>
            <Button size="small" onClick={handleNewSession}>New session</Button>
          </Stack>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">{sessionMessage}</Typography>
          {savedSessions.length > 0 && <FormControl size="small" sx={{ minWidth: 240, ml: { sm: 'auto' } }}><InputLabel id="microscopy-session-label">My lab sessions</InputLabel><Select labelId="microscopy-session-label" label="My lab sessions" value={sessionId || ''} onChange={(event) => handleResumeSession(event.target.value)}>{savedSessions.map((saved) => <MenuItem key={saved._id} value={saved._id}>{saved.title} · {saved.updatedAt ? new Date(saved.updatedAt).toLocaleDateString() : 'saved'}</MenuItem>)}</Select></FormControl>}
        </Stack>

        <Alert severity="info" className="micro-intro" onClose={() => setShowOnboarding(false)}>
          Start on low power, secure the slide, then use coarse and fine focus. Drag in the eyepiece to pan; choose Measure and click two points to estimate size.
        </Alert>

        <PracticalAssessment practicalType="microscopy" title={`${PRACTICAL_MODES.find((item) => item.id === mode)?.label || 'Microscopy'} Assessment Question`} questionTag={PRACTICAL_MODES.find((item) => item.id === mode)?.assessmentTag} />
        <PracticalInstructions
          title="Microscopy practical instructions"
          introduction="Prepare and examine the selected slide systematically. Start at low power, centre the specimen, balance illumination, and only then increase magnification."
          sections={[
            { title: 'A. Prepare the microscope and slide', steps: [
              'Place the microscope on a stable bench. Switch on the illuminator at a low setting and rotate the 4x scanning objective into position.',
              'Select the required specimen slide and check its stain and expected structures in the slide information panel.',
              'Place the slide flat on the stage with the specimen over the opening. Secure it with both stage clips.',
              'Centre the specimen using the stage controls. Adjust lamp brightness, condenser, and diaphragm until the field is bright but not washed out.'
            ] },
            { title: 'B. Focus and examine at increasing power', steps: [
              'Begin with the 4x objective. Use coarse focus to bring the specimen into view, then use fine focus to sharpen it.',
              'Centre the feature of interest before rotating to the 10x objective. At higher power, use fine focus only and make small adjustments.',
              'Repeat at 40x if suitable. Improve contrast with the diaphragm and condenser rather than over-brightening the field.',
              'Calculate total magnification as eyepiece magnification multiplied by objective magnification. With a 10x eyepiece, 4x, 10x, 40x, and 100x objectives give 40x, 100x, 400x, and 1000x.',
              'Use the 100x objective only with the oil-immersion setting. Apply immersion oil as directed, and clean the objective before returning to a dry objective.'
            ] },
            { title: 'C. Measure, record, and report', steps: [
              'Use Measure only after the image is in focus. Place the endpoints across the structure and record the displayed value and uncertainty.',
              'Pan the stage to inspect different regions, then return to the same feature when comparing magnifications.',
              'Record specimen name, objective and total magnification, stain, illumination conditions, structures seen, and measurements in the notebook.',
              'Describe observations objectively. Add a labelled scientific drawing or captured field image and a conclusion linked to the evidence.',
              'Complete the guided checklist for slide security, low-power focus, fine focus, illumination, and recorded evidence.'
            ] }
          ]}
          safety={[
            'Carry the microscope upright with two hands and keep the slide secured while moving the stage.',
            'At high power, do not use coarse focus; the objective can strike the slide.',
            'Handle prepared slides by their edges and follow the displayed cleaning/disposal guidance.',
            'Switch off the lamp after use and clean immersion oil from the objective with lens paper only.'
          ]}
        />

        <div className="micro-workbench">
          <section className="micro-controls-column" aria-label="Microscope and slide controls">
            <Paper className="micro-panel micro-slide-panel" elevation={0}>
              <Typography variant="h6" fontWeight={800}>Slide box</Typography>
              <FormControl size="small" fullWidth sx={{ mt: 1.5 }}>
                <InputLabel id="micro-mode-label">Practical mode</InputLabel>
                <Select labelId="micro-mode-label" label="Practical mode" value={mode} onChange={(event) => setMode(event.target.value as PracticalMode)}>
                  {PRACTICAL_MODES.map((item) => <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth sx={{ mt: 1.5 }}>
                <InputLabel id="micro-slide-label">Specimen slide</InputLabel>
                <Select labelId="micro-slide-label" label="Specimen slide" value={slideId} onChange={(event) => setSlideId(event.target.value)}>
                  {slideCatalog.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                </Select>
              </FormControl>
              <Typography variant="caption" display="block" sx={{ mt: 1.5 }}><strong>Stain:</strong> {slide.stain}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{slide.expectedObservation}</Typography>
              <div className="structure-chips">{slide.structures.map((structure) => <Chip key={structure} label={structure} size="small" variant="outlined" />)}</div>
            </Paper>
            <Microscope objective={objective} lampOn={lampOn} onLampToggle={() => setLampOn((value) => !value)} onObjectiveChange={handleObjectiveChange} />
            <Paper className="micro-panel" elevation={0}>
              <Typography variant="subtitle1" fontWeight={800}>Stage travel</Typography>
              <Typography variant="caption">X axis · pan specimen left/right</Typography><Slider aria-label="Stage X position" min={-240} max={240} defaultValue={0} onChange={(_, value) => setStagePan((current) => ({ ...current, x: Array.isArray(value) ? value[0] : value }))} />
              <Typography variant="caption">Y axis · pan specimen up/down</Typography><Slider aria-label="Stage Y position" min={-240} max={240} defaultValue={0} onChange={(_, value) => setStagePan((current) => ({ ...current, y: Array.isArray(value) ? value[0] : value }))} />
            </Paper>
          </section>

          <section className="micro-view-column" aria-label="Microscope field and optical controls">
            <Paper className="micro-panel eyepiece-panel" elevation={0}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
                <Box><Typography variant="h6" fontWeight={800}>Eyepiece view</Typography><Typography variant="body2" color="text.secondary">{slide.name} · objective {objective}× · eyepiece 10×</Typography></Box>
                <Button size="small" variant={measuring ? 'contained' : 'outlined'} onClick={() => { setMeasuring((value) => !value); setMeasurement(null); }}> {measuring ? 'Cancel measure' : 'Measure'} </Button>
              </Stack>
              <FieldOfView slide={slide} objective={objective} focusOffsetUm={focusOffsetUm} lighting={{ lamp, diaphragm, condenser, enabled: lampOn }} measuring={measuring} onMeasurement={handleMeasurement} onSnapshot={handleSnapshot} stagePan={stagePan} onStagePanChange={setStagePan} oilImmersion={oilImmersion} />
              {objective === 100 && !oilImmersion && <Alert severity="warning" sx={{ mt: 2 }} action={<Button color="inherit" size="small" onClick={() => setOilImmersion(true)}>Add oil</Button>}>Oil immersion is required before using the 100× objective.</Alert>}
              {objective === 100 && oilImmersion && <Alert severity="success" sx={{ mt: 2 }}>Immersion oil applied. Clean the objective before returning to dry objectives.</Alert>}
              {measurement && <Alert severity="success" sx={{ mt: 2 }}>Measured length: {measurement.distanceMicrometres.toFixed(1)} ± {measurement.uncertaintyMicrometres.toFixed(1)} µm</Alert>}
            </Paper>

            <Paper className="micro-panel control-panel" elevation={0}>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>Optical controls</Typography>
              <div className="micro-slider-grid">
                <Tooltip title="Coarse focus moves the stage through large vertical steps; use only at low power." arrow><div className="micro-slider"><Typography variant="caption">Coarse focus · {focusOffsetUm.toFixed(1)} µm</Typography><Slider aria-label="Coarse focus" value={focusOffsetUm} min={-8} max={8} step={0.5} onChange={(_, value) => setFocusOffsetUm(Array.isArray(value) ? value[0] : value)} /></div></Tooltip>
                <Tooltip title="Fine focus makes small axial adjustments to sharpen the focal plane." arrow><div className="micro-slider"><Typography variant="caption">Fine focus</Typography><Slider aria-label="Fine focus" value={focusOffsetUm} min={-2} max={2} step={0.05} onChange={(_, value) => setFocusOffsetUm(Array.isArray(value) ? value[0] : value)} /></div></Tooltip>
                <Tooltip title="Lamp brightness controls the illuminator output." arrow><div className="micro-slider"><Typography variant="caption">Lamp brightness · {Math.round(lamp * 100)}%</Typography><Slider aria-label="Lamp brightness" disabled={!lampOn} value={lamp} min={0} max={1} step={0.01} onChange={(_, value) => setLamp(Array.isArray(value) ? value[0] : value)} /></div></Tooltip>
                <Tooltip title="Closing the iris diaphragm reduces light and increases contrast." arrow><div className="micro-slider"><Typography variant="caption">Diaphragm · {Math.round(diaphragm * 100)}%</Typography><Slider aria-label="Diaphragm opening" value={diaphragm} min={0} max={1} step={0.01} onChange={(_, value) => setDiaphragm(Array.isArray(value) ? value[0] : value)} /></div></Tooltip>
                <Tooltip title="Condenser height helps focus illumination onto the specimen." arrow><div className="micro-slider"><Typography variant="caption">Condenser height · {Math.round(condenser * 100)}%</Typography><Slider aria-label="Condenser height" value={condenser} min={0} max={1} step={0.01} onChange={(_, value) => setCondenser(Array.isArray(value) ? value[0] : value)} /></div></Tooltip>
                <div className="micro-slider"><Typography variant="caption">Stage clips</Typography><Button size="small" variant={stageClipsOn ? 'contained' : 'outlined'} onClick={() => setStageClipsOn((value) => !value)} aria-pressed={stageClipsOn}>{stageClipsOn ? 'Slide secured' : 'Slide released'}</Button></div>
              </div>
            </Paper>
          </section>
        </div>

        <StepGuide mode={mode} slide={slide} objective={objective} focusOffsetUm={focusOffsetUm} stageClipsOn={stageClipsOn} lampOn={lampOn} lamp={lamp} diaphragm={diaphragm} observationCount={observations.length} completedSteps={completedSteps} onCompletedStepsChange={setCompletedSteps} />
        <Notebook observations={observations} slide={slide} objective={objective} lighting={{ lamp, diaphragm, condenser, enabled: lampOn }} measurement={measurement} snapshotDraft={snapshotDraft} conclusion={conclusion} onConclusionChange={setConclusion} onAdd={handleAddObservation} onUpdate={handleUpdateObservation} onDelete={handleDeleteObservation} onReportGenerated={handleReportGenerated} />
        <DataAnalytics observations={observations} completedSteps={completedSteps} />
        {user?.role === 'admin' && <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}><SlideLibraryAdmin slides={slideCatalog} onSlidesChanged={refreshSlideCatalog} /></Box>}
      </Container>
      <Snackbar open={showOnboarding} message="Welcome. Start with a low-power objective and work up to higher magnification." action={<Button color="secondary" size="small" onClick={acknowledgeOnboarding}>Got it</Button>} />
      <Snackbar open={resetNotice} autoHideDuration={2500} onClose={() => setResetNotice(false)} message="Microscope controls reset." />
    </Box>
  );
};

export default MicroscopyLab;
